import { Box, 
         Chip, 
         IconButton, 
         Typography, 
         useTheme 
       }                                    from "@mui/material"
import React                                from "react"
import { APIData }                          from "types/cms/frontendTypes"
import { ChevronDown, 
         ChevronUp, 
         GripVertical, 
         Pencil, 
         Trash2,
         ChevronsRight,
         CirclePlus
       }                                    from "lucide-react"
import { getLocalizedTemplateQuestionTitle, 
         getLocalizedQuestionTitle 
       }                                    from "../utils/SurveyTemplateLocalizationUtils"
import { CMS_DEFAULT_LOCALE }               from "store/dataReducers/cmsPilotSlice"

export interface QuestionTreeNode {
  templateQuestion : APIData<'api::survey-template.survey-template'>['templateQuestions'][0]
  question : APIData<'api::survey-question.survey-question'>
  children : QuestionTreeNode[]
  questionNumber : string
  level : number
  parentChoiceValue ?: string
}

interface SurveyTemplateQuestionItemProps {
  node: QuestionTreeNode
  parentQuestion?: APIData<'api::survey-question.survey-question'> | null
  isExpanded: boolean
  onToggleExpand: (questionNumber: string) => void
  isDragging: boolean
  isDragOver: boolean
  onDragStart: (e: React.DragEvent, questionNumber: string) => void
  onDragOver: (e: React.DragEvent, questionNumber: string) => void
  onDragLeave: () => void
  onDrop: (e: React.DragEvent, questionNumber: string) => void
  onEditQuestion: (questionUid: string) => void
  onDelete: (questionUid: string) => void
  onAddChildQuestion: (parentQuestionUid: string, parentChoiceValue: string, parentChoiceTitle: string, parentChoiceReadMapper: string) => void
  renderChild: (childNode: QuestionTreeNode, parentQuestion: APIData<'api::survey-question.survey-question'>) => React.ReactNode
  editable: boolean
  selectedLocale: string
  displayTemplate: APIData<'api::survey-template.survey-template'>
}

export const SurveyTemplateQuestionItem = ({
  node,
  parentQuestion,
  isExpanded,
  onToggleExpand,
  isDragging,
  isDragOver,
  onDragStart,
  onDragOver,
  onDragLeave,
  onDrop,
  onEditQuestion,
  onDelete,
  onAddChildQuestion,
  renderChild,
  editable,
  selectedLocale,
  displayTemplate,
}: SurveyTemplateQuestionItemProps) => {
  const theme = useTheme()
  
  const { templateQuestion, question, children, questionNumber, level } = node
  
  // Get localized values for the selected locale
  const localizedTemplateTitle = getLocalizedTemplateQuestionTitle(displayTemplate, templateQuestion, selectedLocale)
  const localizedQuestionTitle = getLocalizedQuestionTitle(question, selectedLocale)
  
  // Check if localized question title exists (for non-default locale)
  const hasLocalizedQuestionTitle = selectedLocale === CMS_DEFAULT_LOCALE 
    ? true // Always has title for default locale
    : (question as any).localizations?.find((l: any) => l.locale === selectedLocale)?.title !== undefined
  
  // Display title: use template override if present, otherwise use question title
  const questionTitle = localizedTemplateTitle || localizedQuestionTitle || question.title || ''
  
  // Check if we're showing default locale title in italics (when localized title doesn't exist)
  const isDefaultTitle = selectedLocale !== CMS_DEFAULT_LOCALE && 
    !localizedTemplateTitle && 
    !hasLocalizedQuestionTitle
  
  // Check if title is overridden: template has a truthy override and it's different from question title
  // Treat undefined, null, and empty string as "no override"
  const isOverridden = !!localizedTemplateTitle && localizedTemplateTitle !== localizedQuestionTitle
  const questionType = question.type
  const choices = question.choices || []
  const isPrimary = level === 0

  const handleDeleteClick = () => {
    onDelete(templateQuestion.questionUid)
  }

  const handleEditQuestionClick = () => {
    onEditQuestion(templateQuestion.questionUid)
  }

  const handleAddChildQuestionClick = (choiceValue: string, choiceTitle: string, choiceReadMapper: string) => {
    onAddChildQuestion(templateQuestion.questionUid, choiceValue, choiceTitle, choiceReadMapper)
  }

  const getInputFieldNote = (questionType: string): string => {
    switch (questionType) {
      case 'BOOLEAN':
        return 'Users will be shown a boolean input field.'
      case 'NUMBER':
        return 'Users will be shown a number input field.'
      case 'TEXT':
      case 'TEXTAREA':
        return 'Users will be shown a text input field.'
      case 'SINGLE':
      case 'MULTIPLE':
      case 'MULTIPLE_WITH_SUB_CHOICES':
        return '' // Choices are shown, no note needed
      default:
        return ''
    }
  }

  const inputFieldNote = getInputFieldNote(questionType)
  // Expand/collapse only controls options/choices, not child questions (which are always visible)
  const hasContentToExpand = choices.length > 0 || inputFieldNote.length > 0

  // Group children by their parent choice value for inline display
  const childrenByChoiceValue = new Map<string, QuestionTreeNode[]>()
  children.forEach(child => {
    if (child.parentChoiceValue) {
      if (!childrenByChoiceValue.has(child.parentChoiceValue)) {
        childrenByChoiceValue.set(child.parentChoiceValue, [])
      }
      childrenByChoiceValue.get(child.parentChoiceValue)!.push(child)
    }
  })

  return (
    <Box 
      key={questionNumber} 
      sx={{ 
        width: '100%',
        opacity: isDragging ? 0.5 : 1,
        borderBottom: isPrimary ? `2px solid ${theme.palette.background.paper}` : 'none',
        borderRadius: isDragOver && isPrimary ? 1 : 0,
      }}
      draggable={isPrimary && editable}
      onDragStart={(e) => isPrimary && editable && onDragStart(e, questionNumber)}
      onDragOver={(e) => isPrimary && editable && onDragOver(e, questionNumber)}
      onDragLeave={onDragLeave}
      onDrop={(e) => isPrimary && editable && onDrop(e, questionNumber)}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 0.5,
          bgcolor: isDragOver && isPrimary ? theme.palette.action.hover : 'transparent',
        }}
      >
        {/* Reorder handle */}
        <IconButton
          size="small"
          sx={{ 
            cursor: isPrimary && editable ? 'grab' : 'default',
            color: theme.palette.text.secondary,
            '&:hover': { color: editable ? theme.palette.primary.main : theme.palette.text.secondary },
            '&:active': { cursor: editable ? 'grabbing' : 'default' }
          }}
          disabled={!isPrimary || !editable}
        >
          <GripVertical size={18} />
        </IconButton>

        {/* Expand/Collapse button */}
        {hasContentToExpand && (
          <IconButton
            size="small"
            onClick={() => onToggleExpand(questionNumber)}
            sx={{ color: theme.palette.text.secondary }}
          >
            {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
          </IconButton>
        )}
        {!hasContentToExpand && <Box sx={{ width: 32 }} />}

        {/* Question content */}
        <Box 
          sx={{ flex: 1}}
        >
          <Box 
            sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, justifyContent: 'space-between', '&:hover': { bgcolor: theme.palette.action.hover } }}
            onClick={() => onToggleExpand(questionNumber)}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body1" sx={{ fontWeight: 500, color: isDefaultTitle ? theme.palette.text.primary : theme.palette.text.secondary, whiteSpace: 'pre-line', fontSize: '0.875rem', fontStyle: isDefaultTitle ? 'italic' : 'normal' }}>
                <span style={{fontWeight: 400, background: theme.palette.divider, padding: '4px 8px', borderRadius: 4, fontSize: '0.775rem', marginRight : '10px' }}>{questionNumber}</span> {questionTitle}
              </Typography>
              {isOverridden && (
                <Chip
                  label="Overridden"
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: '0.7rem',
                    bgcolor: theme.palette.primary.main,
                    color: 'white',
                  }}
                />
              )}
            </Box>
            {/* Action buttons */}
            {(
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <IconButton
                  disabled={!editable}
                  size="small"
                  sx={{ 
                    color: theme.palette.text.secondary,
                    '&:hover': { bgcolor: theme.palette.action.hover }
                  }}
                  onClick={handleEditQuestionClick}
                >
                  <Pencil size={16} />
                </IconButton>
                <IconButton
                  disabled={!editable}
                  size="small"
                  sx={{ 
                    color: theme.palette.error.main,
                    '&:hover': { bgcolor: theme.palette.error.light + '20' }
                  }}
                  onClick={handleDeleteClick}
                >
                  <Trash2 size={16} />
                </IconButton>
              </Box>
            )}
          </Box>

          {/* Choices list */}
          {choices.length > 0 && isExpanded && (
            <Box 
              sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                gap: 1, 
                mb: 1,
                pl: 2,
                borderLeft: `2px solid ${theme.palette.divider}`,
              }}
            >
              {[...choices]
                .sort((a, b) => (a.order || 0) - (b.order || 0))
                .map((choice, idx) => {
                  const choiceLabel = `${choice.title} (${choice.readMapper})`
                  
                  // Count child questions for this choice
                  const choiceValue = choice.value
                  const childCount = templateQuestion.branchingRules?.filter(
                    rule => rule.parentChoiceValue === choiceValue
                  ).length || 0
                  
                  // Don't show CirclePlus button if question is at level 2 (sub-child level)
                  // Allow multiple children per choice, so we don't check if child exists
                  const canAddChild = level < 2
                  
                  return (
                    <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip
                      key={idx}
                      label={choiceLabel}
                      size="small"
                      variant="outlined"
                      sx={{
                        color: theme.palette.text.secondary,
                        fontSize: '0.75rem',
                        borderColor: theme.palette.divider,
                        bgcolor: theme.palette.background.paper,
                        width: 'fit-content',
                      }}
                    />
                    {childCount > 0 && (
                      <Chip
                        label={`${childCount} child${childCount > 1 ? 'ren' : ''}`}
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: '0.7rem',
                          bgcolor: theme.palette.primary.main,
                          color: 'white',
                        }}
                      />
                    )}
                    {canAddChild && (
                      <IconButton
                        disabled={!editable}
                        size="small"
                        sx={{ 
                          color: theme.palette.primary.main,
                          '&:hover': { bgcolor: theme.palette.primary.light + '20' }
                        }}
                        onClick={() => handleAddChildQuestionClick(choiceValue, choice.title, choice.readMapper || '')}
                      >
                        <CirclePlus size={16} />
                      </IconButton>
                    )}
                    </Box>
                  )
                })}
            </Box>
          )}

          {/* Child questions grouped by triggering choice - always visible */}
          {children.length > 0 && (
            <Box sx={{ mt: 1 }}>
              {Array.from(childrenByChoiceValue.entries())
                .sort((a, b) => {
                  // Sort by the order of choices in the parent question
                  const aIndex = choices.findIndex(c => c.value === a[0])
                  const bIndex = choices.findIndex(c => c.value === b[0])
                  if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex
                  if (aIndex !== -1) return -1
                  if (bIndex !== -1) return 1
                  return 0
                })
                .map(([parentChoiceValue, childNodes]) => {

                  // Show choice title and value in the chip
                  const choice = choices.find(c => c.value === parentChoiceValue)
                  const triggerChipLabel = choice ? `${choice.title} (${choice.readMapper})` : parentChoiceValue
                  
                  return (
                    <Box 
                      key={parentChoiceValue} 
                      sx={{ pl: 2, mb: 1, borderTop: `2px solid ${theme.palette.background.paper}`, pt: childNodes.length > 0 ? 1 : 0 }}
                    >
                      {/* Triggering choice chip */}
                      <Box sx={{ display: 'flex', mb: 1 }}>
                        <ChevronsRight size={16} style={{ marginTop : 4, marginRight : 4 }} />
                        <Chip
                          label={triggerChipLabel}
                          size="small"
                          variant="outlined"
                          sx={{
                            color: theme.palette.text.secondary,
                            fontSize: '0.75rem',
                            borderColor: theme.palette.divider,
                            bgcolor: theme.palette.background.paper,
                            width : 'fit-content',
                          }}
                        />
                      </Box>
                      {/* Child questions */}
                      <Box>
                        {childNodes.map(child => renderChild(child, question))}
                      </Box>
                    </Box>
                  )
                })}
            </Box>
          )}

          {/* Input field note */}
          {inputFieldNote && isExpanded && (
            <Box sx={{ width: '100%', textAlign: 'left' }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              {inputFieldNote}
            </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  )
}

